import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Container,
	Divider,
	Grid,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import logo from 'assets/logo_normal.svg';
import { IPassengerDetail } from 'features/passengers/types';
import {
	loadPassengerWithAgencyContext,
	parseAgencyIdFromSearch,
} from 'features/passengers/helpers/passengerAgencyContext';
import { getAgencyIdOrDefault } from 'features/auth/constants';
import { getInvoicesByPassenger, getPassengerInvoiceSummary } from 'features/invoices/services';
import { IInvoiceSummary, InvoiceStatusEnum, IPassengerInvoiceSummary } from 'features/invoices/types';
import {
	TYPE_LABEL,
	formatAmount,
	formatDate,
	invoiceOverdueDays,
	pendingAmountInMainCurrency,
} from 'features/invoices/uiHelpers';
import { formatDetailDisplayName } from 'features/passengers/helpers/detailView';
import routes from 'constants/routes';

/**
 * Estado de cuenta en pantalla completa, pensado para imprimir / guardar como
 * PDF y enviarle al cliente: logo, fecha de emisión, datos del cliente,
 * resumen de saldos y el detalle de documentos con saldo.
 *
 * Los importes se muestran en la moneda original de cada documento (COP para
 * órdenes de Colombia) con su equivalente USD como referencia. El resumen se
 * calcula por moneda desde los documentos; si el listado quedó truncado se cae
 * al summary en USD de la API, que es completo.
 */

interface ICurrencyTotals {
	currency: string;
	pending: number;
	overdue: number;
	notDue: number;
}

const round2 = (v: number): number => Number(v.toFixed(2));

const FETCH_PAGE_SIZE = 100;
const MAX_PAGES = 5;

export const PassengerAccountStatementPage = (): JSX.Element => {
	const { id } = useParams<{ id: string }>();
	const location = useLocation();
	const dispatch = useDispatch();
	const auth = useSelector((state: RootState) => state.auth);

	const [passengerDetail, setPassengerDetail] = useState<IPassengerDetail | null>(null);
	const [invoices, setInvoices] = useState<IInvoiceSummary[]>([]);
	const [summary, setSummary] = useState<IPassengerInvoiceSummary | null>(null);
	const [truncated, setTruncated] = useState(false);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		let cancelled = false;

		const load = async (): Promise<void> => {
			setLoading(true);
			setError(null);

			try {
				// Primero se resuelve la BU del cliente (puede cambiar el ambiente);
				// recién después se piden facturas/summary, ya con el apiKey correcto.
				const { data: detailData } = await loadPassengerWithAgencyContext({
					passengerId: id,
					preferredAgencyId: parseAgencyIdFromSearch(location.search),
					userAgencyIds: auth.user?.agencies.map((agency) => agency.agencyId) ?? [],
					currentCountry: auth.country,
					dispatch,
				});

				const [summaryResponse, allInvoices] = await Promise.all([
					getPassengerInvoiceSummary(id),
					(async () => {
						const collected: IInvoiceSummary[] = [];
						for (let page = 1; page <= MAX_PAGES; page++) {
							const response = await getInvoicesByPassenger(id, {
								page,
								size: FETCH_PAGE_SIZE,
								sort: 'dueDate',
								status: [InvoiceStatusEnum.Issued],
								hasPendingBalance: true,
							});
							collected.push(...response.elements);
							if (collected.length >= response.total) return { collected, truncated: false };
						}
						return { collected, truncated: true };
					})(),
				]);

				if (cancelled) return;
				setPassengerDetail(detailData);
				setSummary(summaryResponse);
				setInvoices(allInvoices.collected);
				setTruncated(allInvoices.truncated);
			} catch {
				if (!cancelled) setError('No se pudo cargar el estado de cuenta.');
			} finally {
				if (!cancelled) setLoading(false);
			}
		};

		load();
		return () => {
			cancelled = true;
		};
	}, [id]);

	const issuedAtLabel = new Date().toLocaleDateString('es-UY', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
	});

	const totalsByCurrency = useMemo((): ICurrencyTotals[] => {
		const map = new Map<string, ICurrencyTotals>();

		for (const invoice of invoices) {
			const entry = map.get(invoice.currency) ?? { currency: invoice.currency, pending: 0, overdue: 0, notDue: 0 };
			entry.pending += invoice.pendingAmount;
			if (invoiceOverdueDays(invoice) > 0) entry.overdue += invoice.pendingAmount;
			else entry.notDue += invoice.pendingAmount;
			map.set(invoice.currency, entry);
		}

		return Array.from(map.values()).map((entry) => ({
			...entry,
			pending: round2(entry.pending),
			overdue: round2(entry.overdue),
			notDue: round2(entry.notDue),
		}));
	}, [invoices]);

	/** Con el listado completo, el resumen sale por moneda; truncado, cae al summary USD de la API. */
	const showTotalsByCurrency = !truncated && totalsByCurrency.length > 0;
	const hasNonUsd = totalsByCurrency.some((t) => t.currency !== 'USD');

	if (loading) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
				<CircularProgress />
			</Box>
		);
	}

	if (error || !passengerDetail) {
		return (
			<Container maxWidth="md" sx={{ py: 6 }}>
				<Typography color="error">{error ?? 'Cliente no encontrado.'}</Typography>
			</Container>
		);
	}

	const clientName = formatDetailDisplayName(passengerDetail);

	return (
		<Box sx={{ bgcolor: { xs: 'grey.100' }, minHeight: '100vh', py: 3, '@media print': { bgcolor: 'white', py: 0 } }}>
			<Container maxWidth="md">
				{/* Toolbar — no se imprime */}
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						mb: 2,
						'@media print': { display: 'none' },
					}}
				>
					<Button
						startIcon={<ArrowBackIcon />}
						onClick={() =>
							(window.location.href = `${routes.passengers.detail.replace(
								':id',
								id,
							)}?from=customers&agencyId=${getAgencyIdOrDefault(auth.country)}`)
						}
					>
						Volver al cliente
					</Button>
					<Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()}>
						Imprimir / Guardar PDF
					</Button>
				</Box>

				<Paper
					elevation={0}
					sx={{
						p: { xs: 3, sm: 5 },
						border: '1px solid rgba(0,0,0,0.08)',
						borderRadius: 2,
						'@media print': { border: 'none', p: 0 },
					}}
				>
					{/* Encabezado */}
					<Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
						<img src={logo} alt="mevuelo.com" style={{ height: 48 }} />
						<Box textAlign="right">
							<Typography variant="h5" sx={{ fontWeight: 700 }}>
								Estado de cuenta
							</Typography>
							<Typography variant="body2" color="text.secondary">
								Emitido el {issuedAtLabel}
							</Typography>
						</Box>
					</Box>

					<Divider sx={{ my: 3 }} />

					{/* Datos del cliente */}
					<Grid container spacing={2} sx={{ mb: 3 }}>
						<Grid item xs={12} sm={6}>
							<Typography variant="caption" color="text.secondary">
								Cliente
							</Typography>
							<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
								{clientName}
							</Typography>
							{passengerDetail.documentNumber && (
								<Typography variant="body2" color="text.secondary">
									{passengerDetail.documentTypeName ?? 'Documento'} {passengerDetail.documentNumber}
								</Typography>
							)}
						</Grid>
						<Grid item xs={12} sm={6} sx={{ textAlign: { sm: 'right' } }}>
							{passengerDetail.email && (
								<Typography variant="body2" color="text.secondary">
									{passengerDetail.email}
								</Typography>
							)}
							{passengerDetail.countryName && (
								<Typography variant="body2" color="text.secondary">
									{passengerDetail.countryName}
								</Typography>
							)}
						</Grid>
					</Grid>

					{/* Resumen de saldos */}
					<Grid container spacing={2} sx={{ mb: 3 }}>
						<Grid item xs={4}>
							<Box sx={{ border: '1px solid rgba(0,0,0,0.12)', borderRadius: 1, p: 1.5 }}>
								<Typography variant="caption" color="text.secondary">
									Saldo total adeudado
								</Typography>
								{showTotalsByCurrency ? (
									totalsByCurrency.map((t) => (
										<Typography key={t.currency} variant="h6">
											{formatAmount(t.pending, t.currency)}
										</Typography>
									))
								) : (
									<Typography variant="h6">{formatAmount(summary?.totalPendingBalance ?? 0, 'USD')}</Typography>
								)}
							</Box>
						</Grid>
						<Grid item xs={4}>
							<Box sx={{ border: '1px solid rgba(0,0,0,0.12)', borderRadius: 1, p: 1.5 }}>
								<Typography variant="caption" color="text.secondary">
									Vencido
								</Typography>
								{showTotalsByCurrency ? (
									totalsByCurrency.map((t) => (
										<Typography key={t.currency} variant="h6" sx={{ color: t.overdue > 0 ? 'error.main' : undefined }}>
											{formatAmount(t.overdue, t.currency)}
										</Typography>
									))
								) : (
									<Typography
										variant="h6"
										sx={{ color: (summary?.overduePendingBalance ?? 0) > 0 ? 'error.main' : undefined }}
									>
										{formatAmount(summary?.overduePendingBalance ?? 0, 'USD')}
									</Typography>
								)}
							</Box>
						</Grid>
						<Grid item xs={4}>
							<Box sx={{ border: '1px solid rgba(0,0,0,0.12)', borderRadius: 1, p: 1.5 }}>
								<Typography variant="caption" color="text.secondary">
									Por vencer
								</Typography>
								{showTotalsByCurrency ? (
									totalsByCurrency.map((t) => (
										<Typography key={t.currency} variant="h6">
											{formatAmount(t.notDue, t.currency)}
										</Typography>
									))
								) : (
									<Typography variant="h6">{formatAmount(summary?.notYetDuePendingBalance ?? 0, 'USD')}</Typography>
								)}
							</Box>
						</Grid>
					</Grid>

					{showTotalsByCurrency && hasNonUsd && summary && (
						<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -2, mb: 3 }}>
							Equivalente total en USD: {formatAmount(summary.totalPendingBalance, 'USD')} (según el tipo de cambio de
							emisión de cada documento).
						</Typography>
					)}

					{/* Detalle de documentos */}
					{invoices.length === 0 ? (
						<Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
							El cliente no tiene documentos con saldo pendiente. ✨
						</Typography>
					) : (
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>Emisión</TableCell>
									<TableCell>Documento</TableCell>
									<TableCell>Vencimiento</TableCell>
									<TableCell align="right">Días vencidos</TableCell>
									<TableCell align="right">Importe</TableCell>
									<TableCell align="right">Saldo</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{invoices.map((invoice) => {
									const days = invoiceOverdueDays(invoice);
									return (
										<TableRow key={invoice.id}>
											<TableCell>{formatDate(invoice.issueDate)}</TableCell>
											<TableCell>
												<Typography variant="body2" sx={{ fontWeight: 600 }}>
													{invoice.invoiceNumber}
												</Typography>
												<Typography variant="caption" color="text.secondary">
													{TYPE_LABEL[invoice.type]}
													{invoice.externalReference ? ` · ${invoice.externalReference}` : ''}
												</Typography>
											</TableCell>
											<TableCell>{formatDate(invoice.dueDate)}</TableCell>
											<TableCell align="right">
												{days > 0 ? (
													<Chip
														size="small"
														color="error"
														label={`${days} días`}
														sx={{ height: 20, '@media print': { border: 'none' } }}
													/>
												) : (
													<Typography variant="body2" color="text.secondary">
														en plazo
													</Typography>
												)}
											</TableCell>
											<TableCell align="right">
												{formatAmount(invoice.total, invoice.currency)}
												{invoice.currency !== 'USD' && (
													<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
														≈ {formatAmount(invoice.totalInMainCurrency, 'USD')}
													</Typography>
												)}
											</TableCell>
											<TableCell align="right">
												<Typography variant="body2" sx={{ fontWeight: 600 }}>
													{formatAmount(invoice.pendingAmount, invoice.currency)}
												</Typography>
												{invoice.currency !== 'USD' && (
													<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
														≈ {formatAmount(pendingAmountInMainCurrency(invoice), 'USD')}
													</Typography>
												)}
											</TableCell>
										</TableRow>
									);
								})}
								<TableRow>
									<TableCell colSpan={5} align="right" sx={{ fontWeight: 700 }}>
										Total adeudado
									</TableCell>
									<TableCell align="right" sx={{ fontWeight: 700 }}>
										{showTotalsByCurrency
											? totalsByCurrency.map((t) => (
													<Typography key={t.currency} variant="body2" sx={{ fontWeight: 700 }}>
														{formatAmount(t.pending, t.currency)}
													</Typography>
											  ))
											: formatAmount(summary?.totalPendingBalance ?? 0, 'USD')}
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					)}

					{truncated && (
						<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
							* El listado muestra los primeros {FETCH_PAGE_SIZE * MAX_PAGES} documentos con saldo.
						</Typography>
					)}

					<Divider sx={{ my: 3 }} />

					<Typography variant="caption" color="text.secondary">
						Documento informativo generado por mevuelo.com el {issuedAtLabel}.{' '}
						{totalsByCurrency.length === 1
							? `Los importes están expresados en ${totalsByCurrency[0].currency}.`
							: totalsByCurrency.length > 1
							? 'Los importes están expresados en la moneda de cada documento; los equivalentes en USD son referenciales.'
							: 'Los importes están expresados en dólares estadounidenses (USD).'}{' '}
						Ante cualquier consulta sobre este estado de cuenta, contactá a tu asesor.
					</Typography>
				</Paper>
			</Container>
		</Box>
	);
};

export default PassengerAccountStatementPage;
